import { Button, styled, Typography } from '@mui/material';
import { KycRiskRating } from 'further-types/kyc';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';

import { kycRiskRatingLabels } from 'further-ui/labels';

const kycRiskRatingLabelsMap = Object.fromEntries(
  kycRiskRatingLabels.map((label) => [label.value, label.label]),
);

const KycButton = styled(Button)(({ theme }) => ({
  padding: 0,
  color: theme.palette.grey[600],
  textDecoration: 'underline',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
  minWidth: 'auto',
}));

type Props = {
  kybDetails: {
    eKyc: {
      riskRating?: KycRiskRating;
      manualApprovalNotes?: string;
      manualApprovalFiles?: Array<{
        path: string;
        name: string;
        previewUrl: string;
      }>;
    };
    answers?: Array<{
      answerText?: string;
      answerFiles: Array<{
        path: string;
        name: string;
        previewUrl: string;
      }>;
    }>;
  };
};

const PreviousKybDetails: React.FC<Props> = ({ kybDetails }) => {
  const files = kybDetails.answers?.flatMap((answer) => answer.answerFiles);
  const manualApprovalFiles = kybDetails.eKyc.manualApprovalFiles;

  const isInformationCompleted = !!kybDetails.answers?.some(
    (answer) => !!answer.answerText || answer.answerFiles.length,
  );

  return (
    <Column fullWidth spacing="md">
      <FormField label="Written responses" spacing="xxs">
        <Typography variant="body2" color="grey.600">
          {isInformationCompleted ? 'Completed' : '--'}
        </Typography>
      </FormField>
      <FormField label="User-uploaded files" spacing="xxs">
        {files?.length ? (
          files.map((file) => (
            <Row spacing="xs" key={file.path}>
              <Typography variant="body2" color="grey.600">
                {file.name}
              </Typography>
              <KycButton variant="text" color="primary" href={file.previewUrl}>
                Download
              </KycButton>
            </Row>
          ))
        ) : (
          <Typography variant="body2" color="grey.600">
            --
          </Typography>
        )}
      </FormField>
      <FormField label="Risk rating" spacing="xs">
        <Row spacing="xxs" centered>
          <Typography variant="body2" color="grey.600">
            {kybDetails.eKyc.riskRating
              ? kycRiskRatingLabelsMap[kybDetails.eKyc.riskRating]
              : kycRiskRatingLabelsMap[KycRiskRating.Low]}
          </Typography>
        </Row>
      </FormField>
      {manualApprovalFiles?.length ? (
        <FormField label="Admin-uploaded files" spacing="xxs">
          {manualApprovalFiles.map((file) => (
            <Row spacing="xs" key={file.path}>
              <Typography variant="body2" color="grey.600">
                {file.name}
              </Typography>
              <KycButton variant="text" color="primary" href={file.previewUrl}>
                Download
              </KycButton>
            </Row>
          ))}
        </FormField>
      ) : null}

      <FormField label="Notes" spacing="xxs">
        <Typography variant="body2" color="grey.600">
          {kybDetails.eKyc.manualApprovalNotes ?? '--'}
        </Typography>
      </FormField>
    </Column>
  );
};

export default PreviousKybDetails;
